import React, { useState, useEffect, useCallback, useRef } from 'react';
import { View, Text, ScrollView, StyleSheet, ActivityIndicator, TouchableOpacity, RefreshControl, useWindowDimensions, TextInput } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { FaBullhorn, FaPlus, FaTwitter, FaTelegram, FaTiktok, FaInstagram, FaHeart, FaComment, FaShare, FaEye, FaTrash, FaBalanceScale, FaCheckCircle, FaTimes, FaCheck, FaSearch, FaArrowLeft, FaUsers, FaChartLine } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import ListsManager from '../services/ListsManager';
import AnalyticsService from '../services/AnalyticsService';
import OrgManager from '../services/OrgManager';
import { useTheme } from '../theme/ThemeContext';
import { useOrg } from '../hooks/useOrg';
import { CampaignDetailPanel } from './CampaignDetailScreen';
import { CampaignComparePanel } from './CampaignCompareScreen';

let T = {};

const PLATFORM_COLORS = {
    twitter: '#1da1f2',
    telegram: '#0088cc',
    tiktok: '#ff0050',
    instagram: '#e1306c',
    facebook_group: '#1877F2',
};

const CampaignsScreen = () => {
    const { theme } = useTheme();
    const { activeOrg, orgs, mode } = useOrg();
    const { width } = useWindowDimensions();
    const isMobile = width < 768;
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    const isDark = theme.bg === '#050507' || (theme.bg && theme.bg.startsWith('#0'));
    T = isDark ? {
        bg:       '#0f1117',
        card:     '#1a1d27',
        surface:  '#22252f',
        border:   '#2e3140',
        text:     '#f1f5f9',
        textSec:  '#94a3b8',
        textMuted:'#64748b',
        primary:  '#38bdf8',
        success:  '#4ade80',
        warning:  '#fbbf24',
        error:    '#f87171',
    } : {
        bg:       '#ffffff',
        card:     '#ffffff',
        surface:  '#f9fafb',
        border:   '#e5e7eb',
        text:     '#111827',
        textSec:  '#374151',
        textMuted:'#9ca3af',
        primary:  '#0288d1',
        success:  '#16a34a',
        warning:  '#d97706',
        error:    '#dc2626',
    };

    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [searchText, setSearchText] = useState('');

    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [compareMode, setCompareMode] = useState(false);
    const [compareIds, setCompareIds] = useState(new Set());

    const [showModal, setShowModal] = useState(false);
    const [newName, setNewName] = useState('');
    const [newDesc, setNewDesc] = useState('');
    const [newVisibility, setNewVisibility] = useState('org');
    const [orgMembers, setOrgMembers] = useState([]);
    const [accessList, setAccessList] = useState([]);
    const [creating, setCreating] = useState(false);

    const [showDetail, setShowDetail] = useState(false);

    // Draggable sidebar width
    const [sidebarW, setSidebarW] = useState(300);
    const dragRef = useRef(null);
    const dragStartRef = useRef(null);

    useEffect(() => {
        if (newVisibility === 'specific' && activeOrg?.id && orgMembers.length === 0) {
            OrgManager.listMembers(activeOrg.id).then(members => {
                setOrgMembers(members || []);
            });
        }
    }, [newVisibility, activeOrg?.id]);

    const toggleAccess = (listenerId) => {
        setAccessList(prev =>
            prev.includes(listenerId) ? prev.filter(id => id !== listenerId) : [...prev, listenerId]
        );
    };

    const syncCampaignAccess = async (campaignId) => {
        const grants = accessList.map(lid => OrgManager.grantCampaignAccess(campaignId, lid));
        await Promise.all(grants);
    };

    const fetchCampaigns = useCallback(async () => {
        try {
            setError(null);
            let result;
            if (mode === 'all' && orgs.length > 0) {
                const perOrg = await Promise.all(
                    orgs.map(o => ListsManager.fetchCampaigns(null, o.id).then(r => r || []))
                );
                const seen = new Set();
                result = perOrg.flat().filter(c => {
                    if (seen.has(c.id)) return false;
                    seen.add(c.id);
                    return true;
                });
            } else {
                result = await ListsManager.fetchCampaigns(null, activeOrg?.id);
            }
            setCampaigns(result || []);
        } catch (e) {
            console.error('Campaigns fetch error:', e);
            setError('Failed to load campaigns');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [activeOrg?.id, mode, orgs]);

    useEffect(() => { fetchCampaigns(); AnalyticsService.logActivity('view_campaigns'); }, [fetchCampaigns]);

    const onRefresh = useCallback(() => {
        setRefreshing(true);
        fetchCampaigns();
    }, [fetchCampaigns]);

    const handleCreate = async () => {
        if (!newName.trim()) return;
        setCreating(true);
        try {
            const created = await ListsManager.createCampaign(newName.trim(), newDesc.trim() || null, newVisibility, activeOrg?.id);
            if (created?.id && newVisibility === 'specific' && accessList.length > 0) {
                await syncCampaignAccess(created.id);
            }
            setNewName('');
            setNewDesc('');
            setNewVisibility('org');
            setAccessList([]);
            setShowModal(false);
            await fetchCampaigns();
            if (created?.id) {
                setSelectedCampaign(created);
                if (isMobile) setShowDetail(true);
            }
        } catch (e) {
            console.error('Create campaign error:', e);
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = async (id, name, e) => {
        if (e) e.stopPropagation();
        if (!window.confirm(`Delete campaign "${name}"? This cannot be undone.`)) return;
        try {
            await ListsManager.deleteCampaign(id);
            setCampaigns(prev => prev.filter(c => c.id !== id));
            if (selectedCampaign?.id === id) {
                setSelectedCampaign(null);
                if (isMobile) setShowDetail(false);
            }
        } catch (e) {
            console.error('Delete campaign error:', e);
        }
    };

    const handleDetailDeleted = (id) => {
        setCampaigns(prev => prev.filter(c => c.id !== id));
        setSelectedCampaign(null);
        if (isMobile) setShowDetail(false);
    };

    const handleSelectCampaign = (campaign) => {
        if (compareMode) {
            setCompareIds(prev => {
                const next = new Set(prev);
                if (next.has(campaign.id)) next.delete(campaign.id);
                else if (next.size < 5) next.add(campaign.id);
                return next;
            });
        } else {
            setSelectedCampaign(campaign);
            if (isMobile) setShowDetail(true);
        }
    };

    const handleEnterCompare = () => {
        const initIds = new Set();
        if (selectedCampaign) initIds.add(selectedCampaign.id);
        setCompareIds(initIds);
        setCompareMode(true);
        setSelectedCampaign(null);
    };

    const handleExitCompare = () => {
        setCompareMode(false);
        setCompareIds(new Set());
    };

    const startDrag = (e) => {
        e.preventDefault();
        dragRef.current = true;
        dragStartRef.current = { x: e.clientX, sidebarW };
        const onMove = (mv) => {
            const rawDx = mv.clientX - dragStartRef.current.x;
            const dx = i18n.language === 'he' ? -rawDx : rawDx;
            setSidebarW(Math.max(220, Math.min(500, dragStartRef.current.sidebarW + dx)));
        };
        const onUp = () => {
            dragRef.current = null;
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    const handleSearch = (query) => {
        setSearchText(query);
    };

    const filteredCampaigns = searchText.trim()
        ? campaigns.filter(c =>
            c.name.toLowerCase().includes(searchText.toLowerCase()) ||
            (c.description && c.description.toLowerCase().includes(searchText.toLowerCase()))
        )
        : campaigns;

    const css = `
        .camp-item { transition: border-color 0.15s, background 0.15s; cursor: pointer; }
        .camp-item:hover { border-color: #7dd3fc !important; background: #f0f9ff !important; }
        .camp-item.sel { border-color: #0288d1 !important; background: #f0f9ff !important; }
        .camp-divider { width: 5px; flex-shrink: 0; cursor: col-resize; background: transparent; position: relative; z-index: 10; transition: background 0.15s; }
        .camp-divider:hover, .camp-divider:active { background: ${T.primary}55; }
        .camp-divider::after { content: ''; position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); width: 1px; height: 32px; background: ${T.border}; border-radius: 1px; }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(128,128,128,0.15); border-radius: 2px; }
    `;

    // ── Left Sidebar ────────────────────────────────────────────────────────
    const sidebarJsx = (
        <div style={{
            width: isMobile ? '100%' : sidebarW,
            flexShrink: 0,
            borderRight: isMobile ? 'none' : `1px solid ${T.border}`,
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            height: '100%',
        }}>
            {/* Header */}
            <div style={{
                padding: '16px 16px 12px',
                flexShrink: 0,
                borderBottom: `1px solid ${T.border}`,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <FaBullhorn size={16} color={T.primary} />
                            <div style={{ color: T.text, fontSize: 17, fontWeight: 700, letterSpacing: 0.3 }}>
                                {t('app.campaigns.title')}
                            </div>
                            <div style={{
                                background: `${T.primary}20`, padding: '2px 8px', borderRadius: 10,
                                fontSize: 11, fontWeight: 700, color: T.primary,
                            }}>
                                {campaigns.length}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                    {compareMode ? (
                        <>
                            <button
                                onClick={() => {/* compare is rendered inline */}}
                                disabled={compareIds.size < 2}
                                style={{
                                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                                    padding: '7px 12px', borderRadius: 20,
                                    background: compareIds.size >= 2 ? T.primary : T.surface,
                                    border: 'none', cursor: compareIds.size >= 2 ? 'pointer' : 'default',
                                    opacity: compareIds.size < 2 ? 0.5 : 1,
                                }}
                            >
                                <FaBalanceScale size={10} color={compareIds.size >= 2 ? '#000' : T.textMuted} />
                                <span style={{ color: compareIds.size >= 2 ? '#000' : T.textMuted, fontSize: 11, fontWeight: 700 }}>
                                    {t('app.campaigns.compare')} {compareIds.size > 0 ? compareIds.size : ''}
                                </span>
                            </button>
                            <button
                                onClick={handleExitCompare}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 4,
                                    padding: '7px 12px', borderRadius: 20,
                                    background: T.surface, border: `1px solid ${T.border}`,
                                    cursor: 'pointer',
                                }}
                            >
                                <FaTimes size={10} color={T.textMuted} />
                                <span style={{ color: T.textMuted, fontSize: 11, fontWeight: 600 }}>{t('app.campaigns.cancel')}</span>
                            </button>
                        </>
                    ) : (
                        <>
                            {campaigns.length >= 2 && (
                                <button
                                    onClick={handleEnterCompare}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: 5,
                                        padding: '7px 12px', borderRadius: 20,
                                        background: `${T.primary}15`, border: `1px solid ${T.primary}40`,
                                        cursor: 'pointer',
                                    }}
                                >
                                    <FaBalanceScale size={10} color={T.primary} />
                                    <span style={{ color: T.primary, fontSize: 11, fontWeight: 600 }}>{t('app.campaigns.compare')}</span>
                                </button>
                            )}
                            <button
                                onClick={() => setShowModal(true)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 5,
                                    padding: '7px 12px', borderRadius: 8,
                                    background: T.primary, border: 'none', cursor: 'pointer',
                                }}
                            >
                                <FaPlus size={10} color="#fff" />
                                <span style={{ color: '#fff', fontSize: 11, fontWeight: 700 }}>{t('app.campaigns.new')}</span>
                            </button>
                        </>
                    )}
                </div>

                {/* Search */}
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    background: T.surface, borderRadius: 8, padding: '8px 12px',
                    border: `1px solid ${T.border}`,
                }}>
                    <FaSearch size={11} color={T.textMuted} />
                    <input
                        value={searchText}
                        onChange={e => handleSearch(e.target.value)}
                        placeholder={t('app.campaigns.searchPlaceholder')}
                        style={{
                            flex: 1, background: 'transparent',
                            border: 'none', outline: 'none',
                            color: T.text, fontSize: 12,
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                        }}
                    />
                    {searchText && (
                        <div onClick={() => handleSearch('')} style={{ cursor: 'pointer' }}>
                            <FaTimes size={10} color={T.textMuted} />
                        </div>
                    )}
                </div>
            </div>

            {/* Campaign list */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '4px 8px 8px' }}>
                {loading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                        justifyContent: 'center', padding: 32, gap: 12 }}>
                        <ActivityIndicator size="small" color={T.primary} />
                        <span style={{ color: T.textMuted, fontSize: 12 }}>{t('app.campaigns.loading')}</span>
                    </div>
                ) : error ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                        justifyContent: 'center', padding: 32, gap: 8 }}>
                        <span style={{ color: T.error, fontSize: 13 }}>{error}</span>
                        <button onClick={fetchCampaigns} style={{
                            background: `${T.primary}20`, border: 'none', borderRadius: 8,
                            padding: '6px 14px', cursor: 'pointer',
                        }}>
                            <span style={{ color: T.primary, fontSize: 12, fontWeight: 600 }}>{t('app.common.retry')}</span>
                        </button>
                    </div>
                ) : filteredCampaigns.length === 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
                        justifyContent: 'center', padding: '40px 16px', gap: 10 }}>
                        <FaBullhorn size={24} color={T.textMuted} />
                        <span style={{ color: T.textMuted, fontSize: 13, textAlign: 'center' }}>
                            {searchText ? t('app.campaigns.noMatchingCampaigns') : t('app.campaigns.noCampaignsYet')}
                        </span>
                        {!searchText && (
                            <button onClick={() => setShowModal(true)} style={{
                                display: 'flex', alignItems: 'center', gap: 5,
                                padding: '7px 14px', borderRadius: 20,
                                background: T.primary, border: 'none', cursor: 'pointer',
                                marginTop: 4,
                            }}>
                                <FaPlus size={10} color="#fff" />
                                <span style={{ color: '#fff', fontSize: 11, fontWeight: 700 }}>{t('app.campaigns.createCampaign')}</span>
                            </button>
                        )}
                    </div>
                ) : (
                    filteredCampaigns.map(c => {
                        const isSelected = !compareMode && selectedCampaign?.id === c.id;
                        const isChecked = compareMode && compareIds.has(c.id);
                        const statusColor = c.status === 'active' ? T.success : c.status === 'paused' ? T.warning : T.textMuted;
                        return (
                            <div
                                key={c.id}
                                className={`camp-item${isSelected ? ' sel' : ''}`}
                                onClick={() => handleSelectCampaign(c)}
                                style={{
                                    borderRadius: 10,
                                    border: `1px solid ${isSelected ? '#0288d1' : isChecked ? '#0288d1' : T.border}`,
                                    marginBottom: 5,
                                    padding: '12px 12px 10px',
                                    background: isSelected ? '#f0f9ff' : isChecked ? '#f0f9ff' : T.card,
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 10,
                                }}
                            >

                                {/* Compare checkbox */}
                                {compareMode && (
                                    <div style={{
                                        width: 22, height: 22, borderRadius: 11, flexShrink: 0,
                                        background: isChecked ? T.primary : T.surface,
                                        border: `2px solid ${isChecked ? T.primary : T.border}`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        marginTop: 1,
                                    }}>
                                        {isChecked && <FaCheckCircle size={12} color="#fff" />}
                                    </div>
                                )}

                                <div style={{ flex: 1, minWidth: 0, paddingLeft: 4 }}>
                                    {/* Name + status */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                                        <div style={{
                                            width: 6, height: 6, borderRadius: 3, flexShrink: 0,
                                            background: statusColor,
                                        }} />
                                        <div style={{
                                            color: T.text, fontSize: 13, fontWeight: 700, lineHeight: '18px',
                                            overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                                            flex: 1,
                                        }}>
                                            {c.name}
                                        </div>
                                        {isSelected && (
                                            <div style={{
                                                width: 18, height: 18, borderRadius: 9, flexShrink: 0,
                                                background: T.primary, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            }}>
                                                <FaCheck size={8} color="#000" />
                                            </div>
                                        )}
                                    </div>
                                    {c.description && (
                                        <div style={{
                                            color: T.textMuted, fontSize: 11, lineHeight: '15px',
                                            overflow: 'hidden', display: '-webkit-box',
                                            WebkitLineClamp: 1, WebkitBoxOrient: 'vertical',
                                            marginBottom: 7,
                                        }}>
                                            {c.description}
                                        </div>
                                    )}
                                    {/* Stats pills */}
                                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                                        <div style={{
                                            padding: '2px 7px', borderRadius: 6,
                                            background: `${T.primary}12`,
                                            border: `1px solid ${T.primary}20`,
                                            fontSize: 10, fontWeight: 700, color: T.primary,
                                            display: 'flex', alignItems: 'center', gap: 3,
                                        }}>
                                            <FaUsers size={7} color={T.primary} />
                                            {c.account_count || 0}
                                        </div>
                                        <div style={{
                                            padding: '2px 7px', borderRadius: 6,
                                            background: T.surface,
                                            border: `1px solid ${T.border}`,
                                            fontSize: 10, fontWeight: 600, color: T.textSec,
                                            display: 'flex', alignItems: 'center', gap: 3,
                                        }}>
                                            <FaChartLine size={7} color={T.textMuted} />
                                            {c.post_count || 0} posts
                                        </div>
                                        {c.status === 'paused' && (
                                            <div style={{
                                                padding: '2px 7px', borderRadius: 6,
                                                background: `${T.warning}15`,
                                                fontSize: 10, fontWeight: 600, color: T.warning,
                                            }}>paused</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );

    // ── Right Area (detail / compare / empty) ───────────────────────────────
    const renderRightArea = () => {
        if (compareMode && compareIds.size >= 2) {
            return (
                <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
                    <CampaignComparePanel
                        campaignIds={Array.from(compareIds)}
                        onBack={handleExitCompare}
                    />
                </div>
            );
        }

        if (compareMode) {
            return (
                <div style={{
                    flex: 1, display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', gap: 16,
                    background: T.bg,
                }}>
                    <div style={{
                        width: 64, height: 64, borderRadius: 20,
                        background: `${T.primary}10`,
                        border: `1px solid ${T.primary}20`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <FaBalanceScale size={28} color={T.textMuted} />
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ color: T.text, fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
                            {t('app.campaigns.selectCampaignsToCompare')}
                        </div>
                        <div style={{ color: T.textMuted, fontSize: 13 }}>
                            {t('app.campaigns.chooseAtLeast2', { count: compareIds.size })}
                        </div>
                    </div>
                </div>
            );
        }

        if (selectedCampaign) {
            return (
                <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
                    <CampaignDetailPanel
                        key={selectedCampaign.id}
                        campaignId={selectedCampaign.id}
                        onDeleted={handleDetailDeleted}
                    />
                </div>
            );
        }

        return (
            <div style={{
                flex: 1, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 16,
                background: T.bg,
            }}>
                <div style={{
                    width: 64, height: 64, borderRadius: 20,
                    background: `${T.primary}10`,
                    border: `1px solid ${T.primary}20`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <FaBullhorn size={28} color={T.textMuted} />
                </div>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ color: T.text, fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
                        {t('app.campaigns.selectACampaign')}
                    </div>
                    <div style={{ color: T.textMuted, fontSize: 13 }}>
                        {t('app.campaigns.selectACampaignDesc')}
                    </div>
                </div>
            </div>
        );
    };

    // ── Mobile detail view ──────────────────────────────────────────────────
    const detailViewJsx = (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {isMobile && (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '12px 16px', borderBottom: `1px solid ${T.border}`,
                    flexShrink: 0,
                }}>
                    <div
                        onClick={() => {
                            if (compareMode) handleExitCompare();
                            else setSelectedCampaign(null);
                            setShowDetail(false);
                        }}
                        style={{ display: 'flex', alignItems: 'center', gap: 6,
                            cursor: 'pointer', color: T.primary, fontSize: 13, fontWeight: 600 }}
                    >
                        <FaArrowLeft size={12} color={T.primary} />
                        {t('app.campaigns.back')}
                    </div>
                </div>
            )}
            {renderRightArea()}
        </div>
    );

    // ── Create Modal ────────────────────────────────────────────────────────
    const createModal = showModal && (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.3)',
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            zIndex: 9999,
        }}>
            <div style={{
                backgroundColor: '#fff',
                borderRadius: 16, border: `1px solid ${T.border}`,
                padding: 32, width: '90%', maxWidth: 480,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            }}>
                <div style={{ color: T.text, fontSize: 24, fontWeight: 800, letterSpacing: -0.5, marginBottom: 8 }}>
                    {t('app.campaigns.newCampaign')}
                </div>
                <div style={{ color: T.textSec, fontSize: 15, marginBottom: 20 }}>
                    {t('app.campaigns.newCampaignDesc')}
                </div>

                <div style={{ marginBottom: 16 }}>
                    <div style={{ color: T.textSec, fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                        {t('app.campaigns.campaignName')}
                    </div>
                    <input
                        placeholder="e.g. Summer Launch"
                        value={newName}
                        onChange={e => setNewName(e.target.value)}
                        autoFocus
                        style={{
                            width: '100%', boxSizing: 'border-box',
                            backgroundColor: T.surface,
                            border: `1px solid ${T.border}`, borderRadius: 12,
                            padding: 16, color: T.text, fontSize: 16,
                            outline: 'none',
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                        }}
                    />
                </div>

                <div style={{ marginBottom: 16 }}>
                    <div style={{ color: T.textSec, fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                        {t('app.campaigns.descriptionOptional')}
                    </div>
                    <textarea
                        placeholder={t('app.campaigns.descriptionPlaceholder')}
                        value={newDesc}
                        onChange={e => setNewDesc(e.target.value)}
                        rows={3}
                        style={{
                            width: '100%', boxSizing: 'border-box',
                            backgroundColor: T.surface,
                            border: `1px solid ${T.border}`, borderRadius: 12,
                            padding: 16, color: T.text, fontSize: 16,
                            outline: 'none', resize: 'vertical',
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                        }}
                    />
                </div>

                <div style={{ marginBottom: 16 }}>
                    <div style={{ color: T.textSec, fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                        {t('app.campaigns.visibility')}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        {[
                            { value: 'private', label: t('app.campaigns.visPrivate'), desc: t('app.campaigns.visPrivateDesc') },
                            { value: 'specific', label: t('app.campaigns.visSpecific'), desc: t('app.campaigns.visSpecificDesc') },
                            { value: 'org', label: t('app.campaigns.visOrg'), desc: t('app.campaigns.visOrgDesc') },
                        ].map(opt => (
                            <div
                                key={opt.value}
                                onClick={() => setNewVisibility(opt.value)}
                                style={{
                                    flex: 1, border: `1px solid ${newVisibility === opt.value ? '#0066cc60' : T.border}`,
                                    borderRadius: 10, padding: '8px 10px', textAlign: 'center',
                                    background: newVisibility === opt.value ? '#0066cc15' : T.surface,
                                    cursor: 'pointer',
                                }}
                            >
                                <div style={{ color: newVisibility === opt.value ? '#0066cc' : T.text, fontWeight: 700, fontSize: 13 }}>{opt.label}</div>
                                <div style={{ color: T.textMuted, fontSize: 11, marginTop: 2 }}>{opt.desc}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {newVisibility === 'specific' && (
                    <div style={{ marginBottom: 16 }}>
                        <div style={{ color: T.textSec, fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                            {t('app.campaigns.whoCanAccess')}
                        </div>
                        {orgMembers.length === 0 ? (
                            <ActivityIndicator size="small" color={T.primary} />
                        ) : (
                            <div style={{ maxHeight: 180, overflow: 'auto' }}>
                                {orgMembers.map(m => {
                                    const selected = accessList.includes(m.listener_id);
                                    return (
                                        <div
                                            key={m.listener_id}
                                            onClick={() => toggleAccess(m.listener_id)}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: 10,
                                                padding: '8px 10px', borderRadius: 8, marginBottom: 4,
                                                background: selected ? '#0066cc15' : T.surface,
                                                border: `1px solid ${selected ? '#0066cc40' : T.border}`,
                                                cursor: 'pointer',
                                            }}
                                        >
                                            <div style={{
                                                width: 28, height: 28, borderRadius: 14,
                                                background: '#0066cc30',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            }}>
                                                <span style={{ color: '#0066cc', fontWeight: 700, fontSize: 12 }}>
                                                    {(m.username || m.email || '?')[0].toUpperCase()}
                                                </span>
                                            </div>
                                            <span style={{ color: T.text, fontSize: 13, flex: 1 }}>{m.username || m.email}</span>
                                            <span style={{ color: T.textMuted, fontSize: 11 }}>{m.role}</span>
                                            <div style={{
                                                width: 20, height: 20, borderRadius: 10,
                                                background: selected ? '#0066cc' : T.surface,
                                                border: `1px solid ${selected ? '#0066cc' : T.border}`,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            }}>
                                                {selected && <FaCheck size={10} color="#fff" />}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 12 }}>
                    <button
                        onClick={() => { setShowModal(false); setNewName(''); setNewDesc(''); setAccessList([]); }}
                        style={{
                            padding: '12px 20px', borderRadius: 12,
                            background: T.surface, border: `1px solid ${T.border}`,
                            color: T.text, fontWeight: 600, fontSize: 15, cursor: 'pointer',
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                        }}
                    >
                        {t('app.campaigns.cancel')}
                    </button>
                    <button
                        onClick={handleCreate}
                        disabled={!newName.trim() || creating}
                        style={{
                            padding: '12px 24px', borderRadius: 8,
                            background: T.primary, border: 'none',
                            color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer',
                            opacity: !newName.trim() ? 0.5 : 1,
                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                        }}
                    >
                        {creating ? t('app.campaigns.creating') : t('app.campaigns.createCampaign')}
                    </button>
                </div>
            </div>
        </div>
    );

    // ── Main Render ─────────────────────────────────────────────────────────
    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: css }} />

            <div style={{
                display: 'flex', flexDirection: 'column',
                height: '100vh', background: T.bg, overflow: 'hidden',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            }}>
                <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                    {isMobile ? (
                        showDetail ? detailViewJsx : sidebarJsx
                    ) : (
                        <>
                            {sidebarJsx}
                            <div className="camp-divider" onMouseDown={startDrag} />
                            {renderRightArea()}
                        </>
                    )}
                </div>
            </div>

            {createModal}
        </>
    );
};

export default CampaignsScreen;
